import { useT } from '@kmgestao/i18n';
import { Badge, PageSectionCard, Skeleton } from '@kmgestao/ui';

import {
  formatCompactInteger,
  formatPercent,
  formatUsd,
  formatWholeNumber,
  type AiKpisProvider,
} from '../../../features/ai-analytics/index.js';

import { getDashboardSeriesColor } from './dashboardTheme.js';

export interface ProvidersCardProps {
  providers: AiKpisProvider[] | undefined;
  totalCalls: number | undefined;
  loading?: boolean;
}

export function ProvidersCard({ providers, totalCalls = 0, loading = false }: ProvidersCardProps) {
  const { t } = useT('dashboard');

  return (
    <PageSectionCard
      title={t('providers.title')}
      description={t('providers.description')}
      contentClassName="space-y-4"
    >
      {loading
        ? Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full" />
          ))
        : null}
      {!loading && (providers ?? []).length === 0 ? (
        <p className="text-base text-muted-foreground">{t('providers.empty')}</p>
      ) : null}
      {!loading ? (
        <ul className="space-y-4">
          {(providers ?? []).map((provider, index) => {
            const share = totalCalls > 0 ? (provider.callsTotal / totalCalls) * 100 : 0;
            const color = getDashboardSeriesColor(index);
            return (
              <li
                key={provider.providerKey}
                className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="size-3 rounded-full"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                      <p className="text-sm font-semibold sm:text-base">{provider.providerKey}</p>
                    </div>
                    <p className="text-sm leading-6 text-muted-foreground">
                      {t('providers.summary', {
                        calls: formatWholeNumber(provider.callsTotal),
                        tokens: formatCompactInteger(provider.tokensTotal),
                      })}
                    </p>
                  </div>
                  <Badge variant="secondary">
                    {t('providers.share', { percent: formatPercent(share) })}
                  </Badge>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                  <div
                    className="h-full rounded-full transition-[width]"
                    style={{ width: `${Math.max(share, 6)}%`, backgroundColor: color }}
                  />
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">{t('providers.cost')}</span>
                  <span className="font-semibold">{formatUsd(provider.costEstimatedUsdTotal)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </PageSectionCard>
  );
}
